import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, DownloadIcon, EyeIcon, EyeOffIcon, HistoryIcon, LayoutGridIcon, ListIcon } from 'lucide-react';
import { MiniaturaDocumento } from '../documentos/MiniaturaDocumento';
import { VisorDocumento } from '../documentos/VisorDocumento';
import { ChipClasificacion } from '../ui/Etiquetas';
import { EstadoVacio } from '../ui/Basicos';
import { useDatos } from '../../contexts/DatosContext';
import type { Documento, Expediente } from '../../types';
import { fechaCorta } from '../../utils/fechas';
import { faltantesDe, formatoEvidencia, tipoDelDocumento } from '../../utils/documentos';
type Estado = 'todos' | 'completos' | 'incompletos' | 'evidencias';
type Vista = 'tabla' | 'galeria';
export function TablaDocumentos({
  documentos,
  expediente
}: {
  documentos: Documento[];
  expediente: Expediente;
}) {
  const {
    tiposDocumento,
    etapasDe
  } = useDatos();
  const etapas = etapasDe(expediente.materia);
  const [tipoFiltro, setTipoFiltro] = useState('todos');
  const [etapaFiltro, setEtapaFiltro] = useState('todas');
  const [estadoFiltro, setEstadoFiltro] = useState<Estado>('todos');
  const [vista, setVista] = useState<Vista>('tabla');
  const [abierto, setAbierto] = useState<{
    doc: Documento;
    vista: 'visor' | 'historial';
  } | null>(null);
  const conEstado = useMemo(() => documentos.map(d => {
    const tipo = tipoDelDocumento(d, tiposDocumento);
    return {
      doc: d,
      faltantes: faltantesDe(d, tipo)
    };
  }), [documentos, tiposDocumento]);
  const tiposEnExpediente = useMemo(() => [...new Set(documentos.map(d => d.tipo))].sort(), [documentos]);
  const filtrados = conEstado.filter(({
    doc,
    faltantes
  }) => {
    if (tipoFiltro !== 'todos' && doc.tipo !== tipoFiltro) return false;
    if (etapaFiltro !== 'todas' && (doc.etapa ?? 'Sin etapa') !== etapaFiltro) return false;
    if (estadoFiltro === 'incompletos' && faltantes.length === 0) return false;
    if (estadoFiltro === 'completos' && faltantes.length > 0) return false;
    if (estadoFiltro === 'evidencias' && !doc.esEvidencia) return false;
    return true;
  });
  const totalFojas = filtrados.reduce((suma, f) => suma + f.doc.fojas, 0);
  const incompletos = conEstado.filter(f => f.faltantes.length > 0).length;
  const claseSelect = 'h-9 rounded border border-gris-200 bg-white px-2.5 text-sm text-gris-700';
  const acciones = (doc: Documento) => <div className="flex items-center justify-end gap-1" data-id="element-578" data-mp="1s922hp78tf6k">
      <button type="button" onClick={() => setAbierto({
      doc,
      vista: 'visor'
    })} aria-label={`Ver ${doc.tipo}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-579" data-mp="1x8593e2vftgn">
        <EyeIcon className="h-4 w-4" data-id="element-580" data-mp="6u4s6ntmpzsd" />
      </button>
      <button type="button" aria-label={`Descargar ${doc.archivo}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-581" data-mp="bu95y635kgew">
        <DownloadIcon className="h-4 w-4" data-id="element-582" data-mp="2l51jejv6s09r" />
      </button>
      <button type="button" onClick={() => setAbierto({
      doc,
      vista: 'historial'
    })} aria-label={`Historial de ${doc.tipo}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-583" data-mp="2z1osfl0eygw1">
        <HistoryIcon className="h-4 w-4" data-id="element-584" data-mp="22acs300cxod2" />
      </button>
    </div>;
  return <>
      <div className="flex flex-wrap items-center gap-2 border-b border-gris-200 px-5 py-3" data-id="element-585" data-mp="2g55fslqeiauq">
        <select aria-label="Filtrar por tipo de documento" value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)} className={claseSelect} data-id="element-586" data-mp="2bbkmh6zr651v">
          <option value="todos" data-id="element-587" data-mp="1jlpneim3f100">Todos los tipos</option>
          {tiposEnExpediente.map(t => <option key={t} value={t} data-id="element-588" data-mp="1wd0cu3yqfsl9">
              {t}
            </option>)}
        </select>

        <select aria-label="Filtrar por etapa" value={etapaFiltro} onChange={e => setEtapaFiltro(e.target.value)} className={claseSelect} data-id="element-589" data-mp="21879wsxhxz17">
          <option value="todas" data-id="element-590" data-mp="2b1ebe2p48uw0">Todas las etapas</option>
          {etapas.map(et => <option key={et.id} value={et.nombre} data-id="element-591" data-mp="2fymuq6m034yu">
              {et.nombre}
            </option>)}
          <option value="Sin etapa" data-id="element-592" data-mp="2jyygxnu0hdcf">Sin etapa asignada</option>
        </select>

        <select aria-label="Filtrar por estado" value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value as Estado)} className={claseSelect} data-id="element-593" data-mp="2p15wsohypdk1">
          <option value="todos" data-id="element-594" data-mp="unefd2te5pe1">Todos los estados</option>
          <option value="completos" data-id="element-595" data-mp="zkelfdq8qs55">Completos</option>
          <option value="incompletos" data-id="element-596" data-mp="zl6h0oju8pvf">Incompletos ({incompletos})</option>
          <option value="evidencias" data-id="element-597" data-mp="s26zypjunaf8">Solo evidencias</option>
        </select>

        <div className="ml-auto flex h-9 items-center rounded border border-gris-200 bg-white p-0.5" data-id="element-598" data-mp="na5j0qbqkviv">
          {[{
          v: 'tabla' as Vista,
          icono: ListIcon,
          etiqueta: 'Vista de tabla'
        }, {
          v: 'galeria' as Vista,
          icono: LayoutGridIcon,
          etiqueta: 'Vista de galería'
        }].map(({
          v,
          icono: Icono,
          etiqueta
        }) => <button key={v} type="button" onClick={() => setVista(v)} aria-label={etiqueta} aria-pressed={vista === v} className={`flex h-8 w-8 items-center justify-center rounded transition-colors duration-150 ${vista === v ? 'bg-marino-800 text-white' : 'text-gris-500 hover:text-marino-700'}`} data-id="element-599" data-mp="1616mnrnsxf8r">
              <Icono className="h-4 w-4" data-id="element-600" data-mp="3gsycvg2qh28b" />
            </button>)}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-2 text-2xs text-gris-500" data-id="element-601" data-mp="28tixnlb7cg78">
        <span data-id="element-602" data-mp="2cuclyfsk3x0n">
          <span className="tabular font-semibold text-gris-700" data-id="element-603" data-mp="2cuueb1c3lr3a">{filtrados.length}</span> de{' '}
          {documentos.length} documentos ·{' '}
          <span className="tabular" data-id="element-604" data-mp="23upkxv3qot84">
            {documentos.filter(d => d.esEvidencia).length} evidencias digitalizadas
          </span>
        </span>
        <span className="tabular" data-id="element-605" data-mp="1p48jibr3u3b8">Total visible: {totalFojas} fojas</span>
      </div>

      {filtrados.length === 0 ? <EstadoVacio mensaje="Ningún documento coincide con los filtros" sugerencia="Ajusta el tipo, la etapa o el estado para ver más resultados." data-id="element-606" data-mp="1tymi0n12cqk4" /> : vista === 'tabla' ? <div className="overflow-x-auto scroll-fino" data-id="element-607" data-mp="1l1u27w333bm2">
          <table className="w-full min-w-[1100px] border-t border-gris-200 text-sm" data-id="element-608" data-mp="1688laj7ja3j7">
            <thead data-id="element-609" data-mp="1b4z1d6jc8z4m">
              <tr className="bg-gris-50 text-left text-2xs uppercase tracking-wide text-gris-500" data-id="element-610" data-mp="1b5il8ssjtxla">
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-611" data-mp="rkqpkaby8j8x">
                  Documento
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-612" data-mp="nkq3c3ilz3rb">
                  Tipo
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-613" data-mp="wjbgfj5b9qkp">
                  Acuse
                </th>
                <th scope="col" className="px-3 py-2.5 text-right font-semibold" data-id="element-614" data-mp="1aiuko0v04hrq">
                  Fojas
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-615" data-mp="16gg79wa3qa7u">
                  Etapa
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-616" data-mp="1k8e7tipqyxse">
                  Cargado por
                </th>
                <th scope="col" className="px-3 py-2.5 font-semibold" data-id="element-617" data-mp="1fbw3ul28r3h4">
                  Estado
                </th>
                <th scope="col" className="px-5 py-2.5 text-right font-semibold" data-id="element-618" data-mp="1p7z8bxkkfe78">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gris-100" data-id="element-619" data-mp="9vepuloqrq4x">
              {filtrados.map(({
            doc,
            faltantes
          }) => <tr key={doc.id} className="align-top hover:bg-gris-50" data-id="element-620" data-mp="ewu7s4vmhgj3">
                  <td className="px-5 py-3" data-id="element-621" data-mp="judp5dnma9f9">
                    <div className="flex items-start gap-3" data-id="element-622" data-mp="osnjxn5fj80c">
                      <button type="button" onClick={() => setAbierto({
                  doc,
                  vista: 'visor'
                })} aria-label={`Abrir ${doc.archivo}`} data-id="element-623" data-mp="os5qsze2d9wn">
                        <MiniaturaDocumento semilla={doc.paginas?.[0]?.id ?? doc.id} origen={doc.origen ?? 'Escáner'} rotacion={doc.paginas?.[0]?.rotacion ?? 0} url={doc.paginas?.[0]?.url} etiqueta={doc.archivo} data-id="element-624" data-mp="13cqyrubmhwr" />
                      </button>
                      <div className="min-w-0" data-id="element-625" data-mp="1qo1ozam2hrkv">
                        {typeof doc.numeroEvidencia === 'number' ? <span className="inline-block rounded bg-marino-800 px-1.5 py-0.5 font-mono text-2xs font-semibold text-white" data-id="element-626" data-mp="1mlwzmo2njh41">
                            {formatoEvidencia(doc.numeroEvidencia)}
                          </span> : <span className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-627" data-mp="1cqmuvskvcvmk">
                            Documento del expediente
                          </span>}
                        <p className="mt-1 max-w-[18rem] text-xs text-gris-700" data-id="element-628" data-mp="12wmhz4jcim3a">
                          {doc.descripcion}
                        </p>
                        <p className="tabular mt-0.5 text-2xs text-gris-400" data-id="element-629" data-mp="tz2fxtdnslrm">
                          {doc.paginas?.length ?? 1} pág. · {doc.origen ?? 'Escáner'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3" data-id="element-630" data-mp="30h7v8uphexc6">
                    <p className="text-xs font-medium text-gris-800" data-id="element-631" data-mp="30fww586zedre">{doc.tipo}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5" data-id="element-632" data-mp="2vhbquucvh3w3">
                      <ChipClasificacion clasificacion={doc.clasificacion} data-id="element-633" data-mp="39a1gd8rm0qxy" />
                      {doc.confidencial && <span className="inline-flex items-center gap-1 text-2xs font-semibold text-marino-700" data-id="element-634" data-mp="359hi21vavha8">
                          <EyeOffIcon className="h-3 w-3" aria-hidden="true" data-id="element-635" data-mp="2hivo1ei4rxj8" />
                          Confidencial
                        </span>}
                    </div>
                  </td>
                  <td className="tabular px-3 py-3 text-xs" data-id="element-636" data-mp="2qh7dveb6aram">
                    {doc.fechaAcuse ? <span className="text-gris-700" data-id="element-637" data-mp="2lmjutuyxup3f">{fechaCorta(doc.fechaAcuse)}</span> : <span className="text-gris-400" data-id="element-638" data-mp="u8bxsajz5x9">—</span>}
                    <p className="text-2xs text-gris-400" data-id="element-639" data-mp="wcbxz5bzks9">Doc: {fechaCorta(doc.fecha)}</p>
                  </td>
                  <td className="tabular px-3 py-3 text-right text-xs text-gris-700" data-id="element-640" data-mp="dclu4b22n0c4">
                    {doc.fojas > 0 ? doc.fojas : '—'}
                  </td>
                  <td className="px-3 py-3 text-xs text-gris-600" data-id="element-641" data-mp="3vk1hka1pjoq4">{doc.etapa ?? '—'}</td>
                  <td className="px-3 py-3" data-id="element-642" data-mp="4cfjqy1o6gh6">
                    <p className="text-xs text-gris-700" data-id="element-643" data-mp="s1i26844odcq">{doc.subidoPor ?? '—'}</p>
                    <p className="tabular text-2xs text-gris-400" data-id="element-644" data-mp="s3uf26v13xix">{doc.subidoEn ?? ''}</p>
                    {(doc.versiones?.length ?? 0) > 1 && <p className="text-2xs font-medium text-marino-600" data-id="element-645" data-mp="iandkx3et1o4">
                        {doc.versiones?.length} versiones
                      </p>}
                  </td>
                  <td className="px-3 py-3" data-id="element-646" data-mp="1azb0sy7py14b">
                    {faltantes.length > 0 ? <span title={`Falta: ${faltantes.join(', ')}`} className="inline-flex items-center gap-1.5 rounded bg-ambar-50 px-2 py-0.5 text-2xs font-semibold text-ambar-700 ring-1 ring-ambar-100" data-id="element-647" data-mp="1fs46ppexbfhz">
                        <AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-648" data-mp="1foc405opuyqf" />
                        Incompleto
                      </span> : <span className="inline-flex items-center rounded bg-exito-50 px-2 py-0.5 text-2xs font-medium text-exito-700 ring-1 ring-exito-100" data-id="element-649" data-mp="1bpmg3j61xysj">
                        Completo
                      </span>}
                  </td>
                  <td className="px-5 py-3" data-id="element-650" data-mp="wz5f20xbxaw6">{acciones(doc)}</td>
                </tr>)}
            </tbody>
          </table>
        </div> : <ul className="grid gap-4 border-t border-gris-200 p-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-651" data-mp="ny7btkupt7en">
          {filtrados.map(({
        doc,
        faltantes
      }) => <li key={doc.id} className="flex flex-col rounded-lg border border-gris-200 bg-white p-3 transition-colors duration-150 hover:border-marino-300" data-id="element-652" data-mp="j0pbgiurk0i9">
              <button type="button" onClick={() => setAbierto({
          doc,
          vista: 'visor'
        })} className="flex justify-center rounded bg-gris-100 py-3" aria-label={`Abrir ${doc.archivo}`} data-id="element-653" data-mp="2zn5g6d69vybl">
                <MiniaturaDocumento semilla={doc.paginas?.[0]?.id ?? doc.id} origen={doc.origen ?? 'Escáner'} rotacion={doc.paginas?.[0]?.rotacion ?? 0} url={doc.paginas?.[0]?.url} ancho={92} alto={120} etiqueta={doc.archivo} data-id="element-654" data-mp="2bwhy4z2n7sbb" />
              </button>

              <div className="mt-3 flex items-center justify-between gap-2" data-id="element-655" data-mp="1s6fw70v6wlzl">
                {typeof doc.numeroEvidencia === 'number' ? <span className="rounded bg-marino-800 px-1.5 py-0.5 font-mono text-2xs font-semibold text-white" data-id="element-656" data-mp="1x4hbdaphw15s">
                    {formatoEvidencia(doc.numeroEvidencia)}
                  </span> : <span className="text-2xs uppercase tracking-wide text-gris-400" data-id="element-657" data-mp="cfwt2ov85j9s">Expediente</span>}
                {faltantes.length > 0 ? <span className="inline-flex items-center gap-1 text-2xs font-semibold text-ambar-700" data-id="element-658" data-mp="7o5bv4xykpwb">
                    <AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-659" data-mp="mgfli80uu83b" />
                    Incompleto
                  </span> : <span className="text-2xs font-medium text-exito-700" data-id="element-660" data-mp="vfaolb8lr22e">Completo</span>}
              </div>

              <p className="mt-1.5 text-sm font-semibold text-marino-900" data-id="element-661" data-mp="rdxlva0t3ezp">{doc.tipo}</p>
              <p className="mt-0.5 line-clamp-2 text-xs text-gris-600" data-id="element-662" data-mp="2x75n0p5ula2o">{doc.descripcion}</p>
              <p className="tabular mt-1.5 text-2xs text-gris-400" data-id="element-663" data-mp="2t5u5z811hx63">
                {doc.etapa ?? 'Sin etapa'} · {doc.fojas > 0 ? `${doc.fojas} fojas` : 'fojas s/d'} ·{' '}
                {doc.paginas?.length ?? 1} pág.
              </p>
              <p className="tabular mt-0.5 text-2xs text-gris-400" data-id="element-664" data-mp="2n4jtnejaft52">
                {doc.subidoPor} · {doc.subidoEn}
              </p>

              <div className="mt-auto flex justify-end pt-2" data-id="element-665" data-mp="2e9lys1wky6b3">{acciones(doc)}</div>
            </li>)}
        </ul>}

      {abierto && <VisorDocumento documento={abierto.doc} vistaInicial={abierto.vista} onCerrar={() => setAbierto(null)} data-id="element-666" data-mp="1zbjv7rpjtktu" />}
    </>;
}